import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Alert, Snackbar, LinearProgress, Typography } from '@mui/material';
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import UploadSection from './UploadSection';
import {
  LOADING_MESSAGES,
  FILE_TYPES,
  ALLOWED_FILE_TYPES,
  ERROR_MESSAGES
} from '../../constants/uploadMessages';

const InvoicesAndFioesUpload = () => {
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const [messageIndex, setMessageIndex] = useState(0);

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  // Prevent default drag behaviors globally
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);

    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  useEffect(() => {
    let messageInterval;

    if (isAnalyzing && progress < 100 && messageIndex < LOADING_MESSAGES.length - 1) {
      messageInterval = setInterval(() => {
        setMessageIndex(prevIndex => {
          if (prevIndex < LOADING_MESSAGES.length - 1) {
            setLoadingMessage(LOADING_MESSAGES[prevIndex + 1]);
            return prevIndex + 1;
          }
          clearInterval(messageInterval);
          return prevIndex;
        });
      }, 3000);
    }

    return () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    };
  }, [isAnalyzing, progress, messageIndex]);

  const validateFiles = useCallback((files) => {
    return Array.from(files).filter(file => {
      const isValidType = ALLOWED_FILE_TYPES.test(file.name);
      if (!isValidType) {
        setError(ERROR_MESSAGES.INVALID_FILE_TYPE);
        return false;
      }
      return true;
    });
  }, []);

  const handleFileChange = useCallback((files, type) => {
    if (!files || files.length === 0) return;

    const validFiles = validateFiles(files);

    if (type === FILE_TYPES.INVOICE) {
      setInvoiceFile(validFiles[0]);
    } else {
      setAdditionalFiles(prev => [...prev, ...validFiles]);
    }
  }, [validateFiles]);

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileChange(e.dataTransfer.files, type);
  }, [handleFileChange]);

  const removeFile = useCallback((index, type) => {
    if (type === FILE_TYPES.INVOICE) {
      setInvoiceFile(null);
    } else {
      setAdditionalFiles(prev => prev.filter((_, i) => i !== index));
    }
  }, []);

  const resetUploadState = () => {
    setIsAnalyzing(false);
    setProgress(0);
    setLoadingMessage(LOADING_MESSAGES[0]);
    setMessageIndex(0);
    setInvoiceFile(null);
    setAdditionalFiles([]);
    setShowSuccess(false);
  };

  const handleSubmit = async () => {
    if (!invoiceFile) {
      setError('נדרש להעלות חשבונית');
      return;
    }

    const formData = new FormData();
    formData.append('invoiceFile', invoiceFile);

    if (additionalFiles.length > 0) {
      additionalFiles.forEach((file) => {
        formData.append('additionalFile', file);
      });
    }

    try {
      setIsAnalyzing(true);
      setError('');
      setProgress(0);

      const response = await execute(
        'POST',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/checkInvoices/process-invoices`,
        formData
      );

      setProgress(100);
      setShowSuccess(true);
      setTimeout(resetUploadState, 1500);

    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        ERROR_MESSAGES.UPLOAD_ERROR
      );
      resetUploadState();
    }
  };


  return (
    <Box
      sx={{
        bgcolor: '#f8fafc',
        minHeight: '100vh',
        width: '100%',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ maxWidth: '100%', width: '100%' }}>
          <>
            <UploadSection
              title="חשבונית *"
              files={invoiceFile}
              type={FILE_TYPES.INVOICE}
              multiple={false}
              disabled={isAnalyzing}
              onDrop={handleDrop}
              onFileChange={(files) => handleFileChange(files, FILE_TYPES.INVOICE)}
              onRemoveFile={(index) => removeFile(index, FILE_TYPES.INVOICE)}
              required={true}
            />

            <UploadSection
              title="מסמכים נלווים"
              files={additionalFiles}
              type={FILE_TYPES.ADDITIONAL}
              multiple={true}
              disabled={isAnalyzing}
              onDrop={handleDrop}
              onFileChange={(files) => handleFileChange(files, FILE_TYPES.ADDITIONAL)}
              onRemoveFile={(index) => removeFile(index, FILE_TYPES.ADDITIONAL)}
              required={false}
            />

            {isAnalyzing && (
              <Box sx={{ width: '100%', mb: 3, mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e3f2fd',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#0563B0',
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  color="primary.dark"
                  align="center"
                  sx={{ mt: 2, fontWeight: 500 }}
                >
                  {loadingMessage}
                </Typography>
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={isAnalyzing || !invoiceFile}
              sx={{
                mt: 3,
                mb: 2,
                height: '48px',
                backgroundColor: '#0563B0',
                '&:hover': {
                  backgroundColor: '#045091',
                },
              }}
            >
              התחל ניתוח
            </Button>
          </>
      </Box>

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ fontWeight: 500, bgcolor: '#0563B0' }}
        >
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={3000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          sx={{ fontWeight: 500 }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoicesAndFioesUpload;